import { LANGUAGES, type Language } from "./language";
import { COMMON_TEXT } from "./text/common";
import { FRONT_TEXT } from "./text/front";
import type { HELP_TEXT } from "./text/help";
import type { HINTS_TEXT } from "./text/hints";
import type { HUD_TEXT } from "./text/hud";

/**
 * The game's namespaces, about seven eighths of the words: `game.ts` registers them from the
 * game's chunk, so the front page's script carries only its own words and the shared ones.
 */
type GameTables = {
  readonly hints: typeof HINTS_TEXT;
  readonly help: typeof HELP_TEXT;
  readonly hud: typeof HUD_TEXT;
};
type Tables = {
  readonly common: typeof COMMON_TEXT;
  readonly front: typeof FRONT_TEXT;
} & GameTables;

/** Every namespace's words in one language, by the name a reader asks for: `phrases().hud.end`. */
export type Messages = { readonly [Name in keyof Tables]: Tables[Name]["en"] };

let gameTables: GameTables | undefined;

export function registerGameTables(tables: GameTables): void {
  gameTables = tables;
}

function gameTable<Name extends keyof GameTables>(name: Name): GameTables[Name] {
  if (!gameTables) {
    throw new Error(`the game's words (${name}) are read before src/i18n/game.ts is loaded`);
  }
  return gameTables[name];
}

function messagesIn(language: Language): Messages {
  return {
    common: COMMON_TEXT[language],
    front: FRONT_TEXT[language],
    get hints() {
      return gameTable("hints")[language];
    },
    get help() {
      return gameTable("help")[language];
    },
    get hud() {
      return gameTable("hud")[language];
    },
  };
}

export const MESSAGES: Readonly<Record<Language, Messages>> = Object.fromEntries(
  LANGUAGES.map((language) => [language, messagesIn(language)]),
) as Record<Language, Messages>;
